import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PrintIcon from '@mui/icons-material/Print';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from '../../app/LanguageContext';
import { useWorkspace } from '../../app/WorkspaceContext';
import { getPolaroidSizesSettings, uploadMedia } from '../../app/api';
import { SavingOverlay } from '../../modules/core/public/SavingOverlay';
import { MediaGallery } from '../../modules/medias/public';
import PolaroidEditor from './private/PolaroidEditor';
import {
  createMediaImageObjectUrl,
  revokeMediaImageObjectUrls,
  useMediaImageSrc,
} from './private/useMediaImageSrc';
import PolaroidPrintSheet from './private/PolaroidPrintSheet';
import PolaroidSizePicker from './private/PolaroidSizePicker';
import {
  createDefaultTransform,
  renderPolaroidBlob,
  renderPolaroidDataUrl,
  scalePreviewOffsetToExport,
} from './private/polaroidCanvas';
import { computePolaroidGrid } from './private/polaroidLayout';
import './polaroidCreator.css';

function mediaDisplayName(media) {
  const title = media?.metadataJSON?.title || media?.filename || '';
  return String(title).replace(/\.[^.]+$/, '') || 'polaroid';
}

export default function PolaroidCreatorPage() {
  const { translate } = useTranslation();
  const { currentWorkspace } = useWorkspace();
  const draftScopeId = currentWorkspace?.id ?? '';

  const [sizes, setSizes] = useState([]);
  const [selectedSizeCode, setSelectedSizeCode] = useState('');
  const [galleryMedias, setGalleryMedias] = useState([]);
  const [transformsByMediaId, setTransformsByMediaId] = useState({});
  const [activeMediaId, setActiveMediaId] = useState(null);
  const [loadingSizes, setLoadingSizes] = useState(true);
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [copying, setCopying] = useState(false);
  const [printItems, setPrintItems] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const selectedSize = useMemo(
    () => sizes.find((s) => s.code === selectedSizeCode) ?? sizes[0] ?? null,
    [sizes, selectedSizeCode],
  );

  const activeIndex = useMemo(
    () => galleryMedias.findIndex((m) => m.id === activeMediaId),
    [galleryMedias, activeMediaId],
  );

  const activeMedia = activeIndex >= 0 ? galleryMedias[activeIndex] : null;
  const activeTransform = activeMediaId
    ? (transformsByMediaId[activeMediaId] ?? createDefaultTransform())
    : createDefaultTransform();
  const {
    src: activeImageSrc,
    loading: activeImageLoading,
    error: activeImageError,
  } = useMediaImageSrc(activeMedia?.id);

  const loadSizes = useCallback(async () => {
    setLoadingSizes(true);
    try {
      const res = await getPolaroidSizesSettings();
      const list = res?.data?.sizes ?? res?.sizes ?? [];
      setSizes(list);
      if (list.length > 0) {
        setSelectedSizeCode((prev) => (list.some((s) => s.code === prev) ? prev : list[0].code));
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setLoadingSizes(false);
    }
  }, []);

  useEffect(() => {
    loadSizes();
  }, [loadSizes]);

  const handleGalleryChange = useCallback((medias) => {
    setGalleryMedias(medias || []);
  }, []);

  useEffect(() => {
    if (galleryMedias.length === 0) {
      setActiveMediaId(null);
      return;
    }
    if (!galleryMedias.some((m) => m.id === activeMediaId)) {
      setActiveMediaId(galleryMedias[galleryMedias.length - 1].id);
    }
  }, [galleryMedias, activeMediaId]);

  const updateTransform = useCallback((transform) => {
    if (!activeMediaId) return;
    setTransformsByMediaId((prev) => ({ ...prev, [activeMediaId]: transform }));
  }, [activeMediaId]);

  const goToPrev = () => {
    if (activeIndex > 0) {
      setActiveMediaId(galleryMedias[activeIndex - 1].id);
    }
  };

  const goToNext = () => {
    if (activeIndex >= 0 && activeIndex < galleryMedias.length - 1) {
      setActiveMediaId(galleryMedias[activeIndex + 1].id);
    }
  };

  const handleSaveMedias = async () => {
    if (!selectedSize || galleryMedias.length === 0) return;
    setSaving(true);
    const objectUrls = [];
    try {
      let saved = 0;
      for (const media of galleryMedias) {
        const imageSrc = await createMediaImageObjectUrl(media.id);
        objectUrls.push(imageSrc);
        const transform = transformsByMediaId[media.id] ?? createDefaultTransform();
        const exportTransform = scalePreviewOffsetToExport(transform, selectedSize);
        const blob = await renderPolaroidBlob(imageSrc, selectedSize, exportTransform, {
          drawBorder: false,
        });
        const name = mediaDisplayName(media);
        const filename = `polaroid-${name}-${selectedSize.code}.png`;
        const file = new File([blob], filename, { type: 'image/png' });
        const formData = new FormData();
        formData.append('file', file);
        formData.append('metadata_key', 'polaroid_creator_output');
        await uploadMedia(formData);
        saved += 1;
      }
      setSnackbar({
        open: true,
        message: translate('polaroid_creator.saved_count', { count: saved }),
        severity: 'success',
      });
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      revokeMediaImageObjectUrls(objectUrls);
      setSaving(false);
    }
  };

  const handleCopyImage = async () => {
    if (!selectedSize || !activeMedia) return;
    setCopying(true);
    let imageSrc = '';
    try {
      imageSrc = await createMediaImageObjectUrl(activeMedia.id);
      const exportTransform = scalePreviewOffsetToExport(activeTransform, selectedSize);
      const blob = await renderPolaroidBlob(imageSrc, selectedSize, exportTransform, {
        drawBorder: false,
      });
      if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
        throw new Error(translate('polaroid_creator.copy_image_unsupported'));
      }
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob }),
      ]);
      setSnackbar({
        open: true,
        message: translate('polaroid_creator.copy_image_success'),
        severity: 'success',
      });
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.message || translate('polaroid_creator.copy_image_error'),
        severity: 'error',
      });
    } finally {
      if (imageSrc) {
        revokeMediaImageObjectUrls([imageSrc]);
      }
      setCopying(false);
    }
  };

  const handlePrint = async () => {
    if (!selectedSize || galleryMedias.length === 0) return;
    setPrinting(true);
    const objectUrls = [];
    try {
      const rendered = await Promise.all(
        galleryMedias.map(async (media) => {
          const imageSrc = await createMediaImageObjectUrl(media.id);
          objectUrls.push(imageSrc);
          const transform = transformsByMediaId[media.id] ?? createDefaultTransform();
          const exportTransform = scalePreviewOffsetToExport(transform, selectedSize);
          const dataUrl = await renderPolaroidDataUrl(imageSrc, selectedSize, exportTransform, {
            drawBorder: true,
          });
          return { dataUrl };
        }),
      );
      setPrintItems(rendered);
      setTimeout(() => {
        window.print();
      }, 300);
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      revokeMediaImageObjectUrls(objectUrls);
      setPrinting(false);
    }
  };

  const gridInfo = selectedSize
    ? computePolaroidGrid(selectedSize.width_mm, selectedSize.height_mm)
    : null;

  return (
    <Box className="polc-root">
      <Box className="polc-screen-only">
        {sizes.length === 0 && !loadingSizes ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {translate('polaroid_creator.no_sizes')}{' '}
            <Link component={RouterLink} to="/polaroid-creator/settings">
              {translate('polaroid_creator.go_settings')}
            </Link>
          </Alert>
        ) : null}

        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
          <Grid item xs={12} md={5} lg={4}>
            <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" gutterBottom>
                {translate('polaroid_creator.size_label')}
              </Typography>
              <PolaroidSizePicker
                sizes={sizes}
                value={selectedSizeCode}
                onChange={setSelectedSizeCode}
                disabled={loadingSizes || sizes.length === 0}
              />
              <Box sx={{ flex: 1 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveMedias}
                  disabled={!selectedSize || galleryMedias.length === 0 || saving}
                  sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
                >
                  {translate('polaroid_creator.save_medias')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  disabled={!selectedSize || galleryMedias.length === 0 || printing}
                  sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
                >
                  {translate('polaroid_creator.print')}
                </Button>
              </Box>
              {gridInfo && galleryMedias.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {translate('polaroid_creator.print_layout_hint', {
                    cols: gridInfo.cols,
                    rows: gridInfo.rows,
                    perPage: gridInfo.perPage,
                  })}
                </Typography>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={7} lg={8}>
            {draftScopeId ? (
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" component="h2" gutterBottom>
                  {translate('products.image_gallery')}
                </Typography>
                <MediaGallery
                  metadataKey="polaroid_creator_draft"
                  metadataValue={draftScopeId}
                  metadataLinkUrl="/polaroid-creator"
                  accept="image/*"
                  hideEmptyPlaceholder
                  onMediaChange={handleGalleryChange}
                />
              </Paper>
            ) : (
              <Alert severity="warning" sx={{ height: '100%' }}>
                {translate('workspace.select_workspace')}
              </Alert>
            )}
          </Grid>
        </Grid>

        {galleryMedias.length > 0 && selectedSize ? (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="subtitle2">
                {translate('polaroid_creator.editor_title')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyImage}
                  disabled={!activeMedia || copying}
                >
                  {translate('polaroid_creator.copy_image')}
                </Button>
                {galleryMedias.length > 1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={goToPrev}
                      disabled={activeIndex <= 0}
                      aria-label={translate('polaroid_creator.prev_photo')}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <Typography variant="caption" color="text.secondary">
                      {translate('polaroid_creator.photo_nav', {
                        current: activeIndex + 1,
                        total: galleryMedias.length,
                      })}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={goToNext}
                      disabled={activeIndex < 0 || activeIndex >= galleryMedias.length - 1}
                      aria-label={translate('polaroid_creator.next_photo')}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
            {activeImageError ? (
              <Alert severity="error" sx={{ mt: 1 }}>
                {activeImageError.message}
              </Alert>
            ) : null}
            {activeMedia && activeImageLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : null}
            {activeMedia && activeImageSrc ? (
              <PolaroidEditor
                key={activeMedia.id}
                imageSrc={activeImageSrc}
                size={selectedSize}
                transform={activeTransform}
                onTransformChange={updateTransform}
              />
            ) : null}
          </Paper>
        ) : galleryMedias.length > 0 && !selectedSize ? (
          <Alert severity="info">{translate('polaroid_creator.select_size_first')}</Alert>
        ) : null}

        {printItems.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              {translate('polaroid_creator.print_preview')}
            </Typography>
            <PolaroidPrintSheet items={printItems} size={selectedSize} />
          </Box>
        )}
      </Box>

      <Box className="polc-print-only">
        <PolaroidPrintSheet items={printItems} size={selectedSize} />
      </Box>

      <SavingOverlay open={saving || printing} messageKey={printing ? 'polaroid_creator.preparing_print' : 'common.saving'} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
